import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperDialogComponent } from '@ricoh-paper/angular';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { HotkeysService, type HotkeyGroup } from '../../../../core/services/hotkeys.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { buildShortcutRows, type ShortcutRow } from '../../../../core/utils/shortcut-format';

interface DisplayGroup {
  id: HotkeyGroup;
  rows: ShortcutRow[];
}

/**
 * Keyboard shortcuts dialog (⌘/) — the discoverable, localised reference
 * for every global shortcut (WCAG 2.4.x keyboard discoverability).
 *
 * The list is driven by HotkeysService's reactive catalogue, so
 * context-scoped registrations (e.g. the knowledge page's ⌘N) appear only
 * while they're actually bound, and admin-aware re-registrations keep
 * their localised descriptions. `<paper-dialog>` supplies the accessible
 * dialog frame: role="dialog", aria-modal, focus trap + restore, Escape.
 *
 * i18n: every string renders through the `transloco` PIPE (not
 * `translate()` in a computed) — the pipe re-renders when a runtime
 * language switch finishes loading the new bundle, whereas a synchronous
 * translate() call races the load and sticks on the fallback language.
 *
 * Global hotkeys are suspended while the dialog is open so ⌘1–6 can't
 * navigate the app underneath the modal.
 */
@Component({
  selector: 'im-shortcuts-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperDialogComponent, TranslocoPipe],
  template: `
    <paper-dialog
      class="ph-no-capture"
      size="md"
      [open]="shell.shortcutsDialogOpen()"
      [title]="'shell.shortcuts_dialog.title' | transloco"
      [description]="'shell.shortcuts_dialog.subtitle' | transloco"
      (closed)="shell.closeShortcutsDialog()">
      <div class="shortcuts" data-testid="shortcuts-dialog-body">
        @for (group of groups(); track group.id) {
          <section class="shortcuts__group" [attr.aria-labelledby]="'shortcuts-group-' + group.id">
            <h3 class="shortcuts__group-title" [id]="'shortcuts-group-' + group.id">
              {{ 'shell.shortcuts_dialog.group_' + group.id | transloco }}
            </h3>
            <ul class="shortcuts__list">
              @for (row of group.rows; track row.descriptionKey + (row.descriptionLabelKey ?? '')) {
                <li class="shortcuts__row">
                  <span class="shortcuts__desc">
                    @if (row.descriptionLabelKey; as labelKey) {
                      {{ row.descriptionKey | transloco: { label: (labelKey | transloco) } }}
                    } @else {
                      {{ row.descriptionKey | transloco }}
                    }
                  </span>
                  <span class="shortcuts__combos">
                    @for (combo of row.combos; track $index) {
                      <kbd class="shortcuts__combo">
                        @for (part of combo; track $index) {
                          <kbd class="shortcuts__key">{{ part }}</kbd>
                        }
                      </kbd>
                    }
                  </span>
                </li>
              }
            </ul>
          </section>
        }
      </div>
    </paper-dialog>
  `,
  // Type is Louis's workspace ramp, read through its custom properties
  // (src/styles/_workspace-type.scss): group titles are the kit's small caps,
  // descriptions body, keys labels at medium weight.
  styles: `
    .shortcuts {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .shortcuts__group-title {
      margin: 0 0 8px;
      font-size: var(--ws-type-micro-size);
      line-height: var(--ws-type-micro-leading);
      font-weight: var(--fw-medium);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-tertiary);
    }

    .shortcuts__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
    }

    .shortcuts__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 7px 0;
      border-bottom: 1px solid var(--border-low);

      &:last-child { border-bottom: none; }
    }

    .shortcuts__desc {
      font-size: var(--ws-type-body-size);
      line-height: var(--ws-type-body-leading);
      color: var(--text);
    }

    .shortcuts__combos {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex: none;
    }

    .shortcuts__combo {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font-family: inherit;
    }

    .shortcuts__key {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border: 1px solid var(--border-med);
      border-bottom-width: 2px;
      border-radius: var(--radius-sm, 6px);
      background: var(--surface-2, var(--btn-bg));
      color: var(--text-secondary);
      font-family: inherit;
      font-size: var(--ws-type-label-size);
      font-weight: var(--fw-medium);
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }
  `,
})
export class ShortcutsDialogComponent {
  protected readonly shell = inject(ShellStateService);
  private readonly hotkeys = inject(HotkeysService);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  /** Disposer for the hotkey suspension held while the dialog is open. */
  private resumeHotkeys: (() => void) | null = null;
  private wasOpen = false;

  /** Grouped rows from the live shortcut catalogue — translation keys
   *  only; the template's transloco pipes resolve them per language. */
  protected readonly groups = computed<DisplayGroup[]>(() => {
    const rows = buildShortcutRows(this.hotkeys.shortcuts(), this.hotkeys.isMac);
    const order: HotkeyGroup[] = ['navigation', 'actions'];
    return order
      .map((id) => ({ id, rows: rows.filter((row) => row.group === id) }))
      .filter((group) => group.rows.length > 0);
  });

  constructor() {
    // Open/close edge: fire ONE analytics event per open with the entry
    // surface, and hold a hotkey suspension while modal so ⌘1–6/⌘K/⌘B
    // can't drive the app underneath the dialog. Escape (paper-dialog)
    // remains the keyboard exit.
    effect(() => {
      const open = this.shell.shortcutsDialogOpen();
      if (open && !this.wasOpen) {
        this.analytics.capture(ANALYTICS_EVENTS.SHELL_SHORTCUTS_DIALOG_OPENED, {
          source: this.shell.shortcutsDialogSource(),
          shortcut_count: this.groups().reduce((n, g) => n + g.rows.length, 0),
        });
        this.resumeHotkeys = this.hotkeys.suspend();
      } else if (!open && this.wasOpen) {
        this.resumeHotkeys?.();
        this.resumeHotkeys = null;
      }
      this.wasOpen = open;
    });

    this.destroyRef.onDestroy(() => {
      this.resumeHotkeys?.();
      this.resumeHotkeys = null;
    });
  }
}
